import axios from 'axios';
import type { LoginRequest, LoginResponse, MeResponse } from '../types/login';
// NOTE: authInterceptor is NOT applied to loginApi to avoid circular imports
// and because /auth/login 401s are handled locally by the login form.

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});

/** Reads the stored access token from localStorage or sessionStorage */
export function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

/** Reads the stored refresh token from localStorage or sessionStorage */
export function getStoredRefreshToken(): string | null {
  return (
    localStorage.getItem('refresh_token') ??
    sessionStorage.getItem('refresh_token')
  );
}

/**
 * Returns which storage the tokens are currently held in.
 * localStorage = rememberMe was true at login time.
 */
export function getTokenStorage(): Storage {
  return localStorage.getItem('access_token') !== null
    ? localStorage
    : sessionStorage;
}

/**
 * Stores access + refresh tokens in the given storage.
 * Called after a successful silent refresh.
 */
export function storeTokens(
  accessToken: string,
  refreshToken: string,
  storage: Storage
): void {
  storage.setItem('access_token', accessToken);
  storage.setItem('refresh_token', refreshToken);
}

/**
 * Removes all token keys from both storages.
 * Called on logout and on session expiry.
 */
export function clearStoredTokens(): void {
  const keys = ['access_token', 'refresh_token', 'refresh_expires_in', 'access_expires_in'];
  keys.forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

// Request interceptor — attach bearer token if available
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T> or surface structured error
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        // Return the unwrapped data payload
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const loginApi = {
  login: (req: LoginRequest): Promise<LoginResponse> =>
    apiClient
      .post<unknown, { data: LoginResponse }>('/auth/login', req)
      .then((res) => res.data),

  refresh: (refreshToken: string): Promise<LoginResponse> =>
    apiClient
      .post<unknown, { data: LoginResponse }>('/auth/refresh', { refreshToken })
      .then((res) => res.data),

  logout: (): Promise<void> => {
    const refreshToken = getStoredRefreshToken();
    return apiClient
      .post('/auth/logout', { refreshToken })
      .then(() => undefined);
  },

  me: (): Promise<MeResponse> =>
    apiClient
      .get<unknown, { data: MeResponse }>('/auth/me')
      .then((res) => res.data),
};
